/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled/macro";

/* ═══════════════════════════════════════════════════════════════
   Digital Partograph — Styled Components
   Theme: Dark teal / slate (#004d40)
   A clinical-grade partograph monitoring interface
   ═══════════════════════════════════════════════════════════════ */

const primary = "#004d40";
const primaryLight = "#00695c";
const primaryBg = "#e0f2f1";
const accent = "#ff6f00";
const accentBg = "#fff3e0";
const danger = "#c62828";
const dangerBg = "#ffebee";
const success = "#2e7d32";
const successBg = "#e8f5e9";
const border = "#cfd8dc";
const bg = "#f9fafb";
const white = "#ffffff";
const text = "#263238";
const textLight = "#546e7a";
const textMuted = "#90a4ae";

/* ─── Page Wrapper ──────────────────────────────────────────── */

export const PGPageWrapper = styled.div({
    padding: "12px 16px 30px",
    background: bg,
    minHeight: "calc(100vh - 140px)",
});

/* ─── Header ────────────────────────────────────────────────── */

export const PGHeader = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 20px",
    background: "linear-gradient(135deg, " + primary + " 0%, " + primaryLight + " 100%)",
    borderRadius: "10px",
    marginBottom: "16px",
    color: white,
    flexWrap: "wrap",
    gap: "12px",
});

export const PGHeaderLeft = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: "2px",
});

export const PGHeaderTitle = styled.h5({
    margin: 0,
    fontSize: "16px",
    fontWeight: 700,
    color: white,
    letterSpacing: "0.3px",
});

export const PGHeaderSub = styled.span({
    fontSize: "11px",
    color: "rgba(255,255,255,0.75)",
    fontWeight: 400,
});

export const PGHeaderStats = styled.div({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
});

export const PGStatChip = styled.span(function(props) {
    const variant = props.variant || "default";
    const colors = {
        active: { bg: "rgba(76,175,80,0.2)", text: "#a5d6a7", border: "rgba(76,175,80,0.3)" },
        warning: { bg: "rgba(255,152,0,0.2)", text: "#ffcc02", border: "rgba(255,152,0,0.3)" },
        danger: { bg: "rgba(244,67,54,0.2)", text: "#ef9a9a", border: "rgba(244,67,54,0.3)" },
        default: { bg: "rgba(255,255,255,0.15)", text: "rgba(255,255,255,0.9)", border: "rgba(255,255,255,0.2)" },
    };
    const c = colors[variant] || colors["default"];
    return {
        padding: "4px 12px",
        borderRadius: "14px",
        fontSize: "11px",
        fontWeight: 600,
        background: c.bg,
        color: c.text,
        border: "1px solid " + c.border,
        whiteSpace: "nowrap",
    };
});

/* ─── Status Badge ──────────────────────────────────────────── */

export const PGStatusBadge = styled.span(function(props) {
    const status = props.status || "active";
    const map = {
        active: { bg: successBg, color: success, border: "#a5d6a7" },
        completed: { bg: primaryBg, color: primary, border: "#80cbc4" },
        abandoned: { bg: dangerBg, color: danger, border: "#ef9a9a" },
    };
    const s = map[status] || map.active;
    return {
        padding: "4px 14px",
        borderRadius: "14px",
        fontSize: "11px",
        fontWeight: 700,
        background: s.bg,
        color: s.color,
        border: "1px solid " + s.border,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
    };
});

/* ─── Tab Bar ───────────────────────────────────────────────── */

export const PGTabBar = styled.div({
    display: "flex",
    gap: "0",
    marginBottom: "16px",
    borderRadius: "8px",
    overflow: "hidden",
    border: "1px solid " + border,
    width: "fit-content",
    "@media (max-width: 600px)": {
        width: "100%",
    },
});

export const PGTabBtn = styled.button(function(props) {
    const active = props.active;
    return {
        padding: "9px 20px",
        fontSize: "12px",
        fontWeight: active ? 700 : 400,
        color: active ? white : text,
        background: active ? primary : white,
        border: "none",
        cursor: "pointer",
        transition: "all 0.15s ease",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        flex: "0 0 auto",
        "&:not(:last-child)": {
            borderRight: "1px solid " + border,
        },
        "&:hover": {
            background: active ? primary : "#f5f5f5",
        },
        "@media (max-width: 600px)": {
            flex: "1 1 0",
            justifyContent: "center",
            padding: "9px 8px",
            fontSize: "11px",
        },
    };
});

export const PGTabBadge = styled.span(function(props) {
    const active = props.active;
    return {
        fontSize: "10px",
        fontWeight: 700,
        padding: "1px 6px",
        borderRadius: "8px",
        background: active ? "rgba(255,255,255,0.25)" : primaryBg,
        color: active ? white : primary,
    };
});

/* ─── Alert Banners ─────────────────────────────────────────── */

export const PGAlertContainer = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginBottom: "16px",
});

export const PGAlertCard = styled.div(function(props) {
    const type = props.type || "warning";
    const map = {
        critical: { bg: "#fff5f5", border: "#fc8181", leftBorder: danger },
        warning: { bg: "#fffbeb", border: "#fbbf24", leftBorder: accent },
        info: { bg: "#eff6ff", border: "#93c5fd", leftBorder: "#2563eb" },
    };
    const c = map[type] || map.warning;
    return {
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        padding: "10px 14px",
        background: c.bg,
        border: "1px solid " + c.border,
        borderLeft: "4px solid " + c.leftBorder,
        borderRadius: "6px",
        animation: "pgSlideIn 0.3s ease",
        "@keyframes pgSlideIn": {
            from: { opacity: 0, transform: "translateX(-10px)" },
            to: { opacity: 1, transform: "translateX(0)" },
        },
    };
});

export const PGAlertIcon = styled.span({
    fontSize: "18px",
    flexShrink: 0,
    lineHeight: 1,
});

export const PGAlertContent = styled.div({
    flex: 1,
});

export const PGAlertTitle = styled.div(function(props) {
    const type = props.type || "warning";
    return {
        fontSize: "12px",
        fontWeight: 700,
        color: type === "critical" ? danger : type === "warning" ? "#92400e" : "#1e40af",
        marginBottom: "1px",
    };
});

export const PGAlertDetail = styled.div({
    fontSize: "11px",
    color: text,
    lineHeight: 1.4,
});

export const PGAlertAction = styled.div({
    fontSize: "10px",
    color: textLight,
    fontStyle: "italic",
    marginTop: "2px",
});

/* ─── Chart Container ───────────────────────────────────────── */

export const PGChartWrapper = styled.div({
    background: white,
    border: "1px solid " + border,
    borderRadius: "10px",
    overflow: "hidden",
    marginBottom: "16px",
});

export const PGChartHeader = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 16px",
    borderBottom: "1px solid " + border,
    background: "#fafafa",
    flexWrap: "wrap",
    gap: "8px",
});

export const PGChartTitle = styled.h6({
    margin: 0,
    fontSize: "13px",
    fontWeight: 700,
    color: primary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
});

export const PGChartLegend = styled.div({
    display: "flex",
    alignItems: "center",
    gap: "14px",
    flexWrap: "wrap",
});

export const PGLegendItem = styled.span(function(props) {
    const color = props.color || primary;
    const dashed = props.dashed;
    return {
        display: "flex",
        alignItems: "center",
        gap: "5px",
        fontSize: "10px",
        fontWeight: 500,
        color: textLight,
        "&::before": {
            content: '""',
            display: "inline-block",
            width: "16px",
            height: "3px",
            background: color,
            borderRadius: "2px",
            borderBottom: dashed ? "2px dashed " + color : "none",
        },
    };
});

export const PGChartBody = styled.div({
    padding: "16px",
    overflowX: "auto",
    "&::-webkit-scrollbar": {
        height: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
        background: border,
        borderRadius: "3px",
    },
});

/* ─── Summary Strip ─────────────────────────────────────────── */

export const PGSummaryStrip = styled.div({
    display: "flex",
    gap: "0",
    padding: "0",
    background: white,
    border: "1px solid " + border,
    borderRadius: "8px",
    marginBottom: "16px",
    overflow: "hidden",
    flexWrap: "wrap",
    "@media (max-width: 768px)": {
        flexDirection: "column",
    },
});

export const PGSummaryItem = styled.div(function(props) {
    const warn = props.warn;
    return {
        flex: "1 1 0",
        padding: "10px 16px",
        borderRight: "1px solid " + border,
        background: warn ? dangerBg : "transparent",
        display: "flex",
        flexDirection: "column",
        gap: "1px",
        minWidth: "100px",
        "&:last-child": {
            borderRight: "none",
        },
        "@media (max-width: 768px)": {
            borderRight: "none",
            borderBottom: "1px solid " + border,
            "&:last-child": { borderBottom: "none" },
        },
    };
});

export const PGSummaryLabel = styled.span({
    fontSize: "9px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: textMuted,
});

export const PGSummaryValue = styled.span(function(props) {
    const color = props.color || text;
    return {
        fontSize: "15px",
        fontWeight: 700,
        color: color,
    };
});

/* ─── Entry Form Card ───────────────────────────────────────── */

export const PGFormCard = styled.div(function(props) {
    const borderColor = props.borderColor || primary;
    return {
        background: white,
        border: "1px solid " + border,
        borderRadius: "8px",
        marginBottom: "14px",
        overflow: "hidden",
        borderTop: "3px solid " + borderColor,
    };
});

export const PGFormHeader = styled.div(function(props) {
    const collapsed = props.collapsed;
    return {
        display: "flex",
        alignItems: "center",
        padding: "10px 14px",
        borderBottom: collapsed ? "none" : "1px solid " + border,
        cursor: "pointer",
        userSelect: "none",
        "&:hover": { background: "rgba(0,77,64,0.02)" },
    };
});

export const PGFormIcon = styled.span(function(props) {
    const color = props.color || primary;
    return {
        width: "26px",
        height: "26px",
        borderRadius: "6px",
        background: color + "15",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "13px",
        flexShrink: 0,
        marginRight: "8px",
    };
});

export const PGFormTitle = styled.h6(function(props) {
    const color = props.color || primary;
    return {
        fontSize: "12px",
        fontWeight: 700,
        color: color,
        margin: 0,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        flex: 1,
    };
});

export const PGFormBody = styled.div(function(props) {
    const collapsed = props.collapsed;
    return {
        padding: collapsed ? "0" : "14px",
        maxHeight: collapsed ? "0" : "2000px",
        overflow: "hidden",
        transition: "all 0.25s ease",
        opacity: collapsed ? 0 : 1,
    };
});

export const PGFormGrid = styled.div(function(props) {
    const columns = props.columns || 4;
    return {
        display: "grid",
        gridTemplateColumns: "repeat(" + columns + ", 1fr)",
        gap: "10px 14px",
        "@media (max-width: 1200px)": {
            gridTemplateColumns: columns > 2 ? "repeat(3, 1fr)" : "repeat(" + columns + ", 1fr)",
        },
        "@media (max-width: 768px)": {
            gridTemplateColumns: "repeat(2, 1fr)",
        },
        "@media (max-width: 480px)": {
            gridTemplateColumns: "1fr",
        },
    };
});

export const PGFormGroup = styled.div(function(props) {
    const span = props.span || 1;
    return {
        gridColumn: "span " + span,
        "@media (max-width: 480px)": {
            gridColumn: "span 1",
        },
    };
});

export const PGLabel = styled.label(function(props) {
    const required = props.required;
    return {
        display: "block",
        fontSize: "11px",
        fontWeight: 500,
        color: textLight,
        marginBottom: "3px",
        "&::after": required ? {
            content: '"*"',
            color: danger,
            marginLeft: "2px",
        } : {},
    };
});

export const PGHint = styled.span(function(props) {
    const warn = props.warn;
    return {
        fontSize: "10px",
        fontWeight: 500,
        color: warn ? danger : textMuted,
        marginTop: "2px",
        display: "block",
    };
});

/* ─── Start Partograph Card ─────────────────────────────────── */

export const PGStartCard = styled.div({
    background: white,
    border: "1px solid " + border,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    borderRadius: "12px",
    padding: "40px 24px",
    textAlign: "center",
    maxWidth: "520px",
    margin: "0 auto",
});

export const PGStartIcon = styled.div({
    fontSize: "48px",
    marginBottom: "12px",
    lineHeight: 1,
});

export const PGStartTitle = styled.h5({
    margin: "0 0 6px",
    fontSize: "17px",
    fontWeight: 700,
    color: text,
});

export const PGStartDesc = styled.p({
    margin: "0 0 20px",
    fontSize: "12px",
    color: textLight,
    lineHeight: 1.5,
});

export const PGStartFields = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    maxWidth: "340px",
    margin: "0 auto 20px",
    textAlign: "left",
});

/* ─── Entries Table ─────────────────────────────────────────── */

export const PGEntriesTable = styled.div({
    background: white,
    border: "1px solid " + border,
    borderRadius: "8px",
    overflow: "hidden",
});

export const PGEntriesHeader = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px",
    borderBottom: "1px solid " + border,
    background: "#fafafa",
});

export const PGEntriesTitle = styled.h6({
    margin: 0,
    fontSize: "12px",
    fontWeight: 700,
    color: primary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
});

export const PGTableWrap = styled.div({
    overflowX: "auto",
    "&::-webkit-scrollbar": {
        height: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
        background: border,
        borderRadius: "3px",
    },
});

export const PGTable = styled.table({
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "11px",
    "& th": {
        padding: "7px 10px",
        fontWeight: 600,
        color: textLight,
        textTransform: "uppercase",
        fontSize: "9px",
        letterSpacing: "0.4px",
        borderBottom: "2px solid " + border,
        background: "#fafafa",
        whiteSpace: "nowrap",
        textAlign: "left",
    },
    "& td": {
        padding: "7px 10px",
        borderBottom: "1px solid #f0f0f0",
        color: text,
        whiteSpace: "nowrap",
    },
    "& tbody tr:hover": {
        background: "rgba(0,77,64,0.02)",
    },
});

export const PGCellWarn = styled.span(function(props) {
    const warn = props.warn;
    return {
        fontWeight: warn ? 700 : 400,
        color: warn ? danger : "inherit",
        background: warn ? dangerBg : "transparent",
        padding: warn ? "1px 6px" : "0",
        borderRadius: warn ? "4px" : "0",
    };
});

export const PGEditBtn = styled.button({
    padding: "3px 10px",
    fontSize: "10px",
    fontWeight: 600,
    color: primary,
    background: primaryBg,
    border: "1px solid #80cbc4",
    borderRadius: "4px",
    cursor: "pointer",
    "&:hover": {
        background: "#b2dfdb",
    },
});

/* ─── Action Buttons ────────────────────────────────────────── */

export const PGActions = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
    padding: "14px 0 0",
    borderTop: "1px solid " + border,
    marginTop: "10px",
    flexWrap: "wrap",
});

export const PGPrimaryBtn = styled.button(function(props) {
    const disabled = props.disabled;
    return {
        padding: "9px 24px",
        background: disabled ? "#b0bec5" : primary,
        color: white,
        border: "none",
        borderRadius: "6px",
        fontSize: "12px",
        fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.15s",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        "&:hover": {
            opacity: disabled ? 1 : 0.9,
            boxShadow: disabled ? "none" : "0 2px 8px rgba(0,0,0,0.15)",
        },
    };
});

export const PGSecondaryBtn = styled.button({
    padding: "9px 18px",
    background: white,
    color: text,
    border: "1px solid " + border,
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: 500,
    cursor: "pointer",
    "&:hover": {
        background: "#f5f5f5",
    },
});

export const PGDangerBtn = styled.button({
    padding: "9px 18px",
    background: dangerBg,
    color: danger,
    border: "1px solid #ef9a9a",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    "&:hover": {
        background: "#ffcdd2",
    },
});

/* ─── No Admission State ────────────────────────────────────── */

export const PGNoAdmission = styled.div({
    background: white,
    border: "1px solid " + border,
    borderRadius: "12px",
    padding: "48px 24px",
    textAlign: "center",
});

export const PGNoAdmissionIcon = styled.div({
    fontSize: "48px",
    marginBottom: "12px",
    lineHeight: 1,
    opacity: 0.5,
});

export const PGNoAdmissionTitle = styled.h5({
    margin: "0 0 8px",
    fontSize: "16px",
    fontWeight: 600,
    color: text,
});

export const PGNoAdmissionDesc = styled.p({
    margin: 0,
    fontSize: "12px",
    color: textLight,
});

/* ─── Loading ───────────────────────────────────────────────── */

export const PGLoading = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 0",
    color: textMuted,
    fontSize: "13px",
});

/* ─── Print Button ──────────────────────────────────────────── */

export const PGPrintBtn = styled.button({
    padding: "6px 14px",
    fontSize: "11px",
    fontWeight: 600,
    color: textLight,
    background: "transparent",
    border: "1px solid " + border,
    borderRadius: "5px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    "&:hover": {
        background: "#f5f5f5",
        color: text,
    },
});
